/**
 * Local cache of each run's materialized state (`RunMaterial`), sealed at rest in IndexedDB,
 * so a reload renders the exact last-known state at once — before the hub answers — and the
 * store then applies only the events after it.
 *
 * The material is private content (goal, checks, git evidence, todos…): it is sealed with the
 * account data key under a local-only context and never sent anywhere. Without encrypted content
 * (or while locked) nothing is kept.
 */
import { type ContentBox, type ContentContext, openContent, sealContent, utf8 } from '@viko/crypto'
import {
  materialFromJson,
  materialInput,
  materialToJson,
  type RunMaterial,
  type TranscriptSync,
  type TranscriptView,
} from '@viko/transcript-ui'
import { useEffect, useState } from 'react'
import { idb } from './idb.js'
import type { RunStore } from './run-store.js'

export interface CachedMaterial {
  seq: number
  epoch: number
  savedAt: number
  material: RunMaterial
}

interface Sealed {
  epoch: number
  box: ContentBox
}

export interface SnapshotKeys {
  /** The newest locally installed epoch key (never provisions or prompts). */
  current(): Promise<{ epoch: number; key: CryptoKey } | undefined>
  forEpoch(epoch: number): Promise<CryptoKey>
}

const KIND = 'local.run-material'

export class MaterialCache {
  private locked = false
  constructor(
    readonly accountId: string,
    private readonly keys: SnapshotKeys,
  ) {}

  lock() {
    this.locked = true
  }

  private slot(runId: string) {
    return `run-material/v1/${JSON.stringify([this.accountId, runId])}`
  }

  private context(runId: string, epoch: number): ContentContext {
    // Local-only domain: never the context of any relay object.
    return {
      version: 1,
      accountId: this.accountId,
      epoch,
      objectId: `run-material:${runId}`.slice(0, 256),
      runId: `local:${runId}`.slice(0, 256),
      seq: 1,
      kind: KIND,
    }
  }

  async load(runId: string): Promise<CachedMaterial | null> {
    if (this.locked) return null
    const sealed = await idb.get<Sealed>(this.slot(runId))
    if (!sealed || !Number.isSafeInteger(sealed.epoch) || sealed.epoch < 1) return null
    const key = await this.keys.forEpoch(sealed.epoch)
    const plain = await openContent(key, this.context(runId, sealed.epoch), sealed.box)
    if (this.locked) return null
    const value = JSON.parse(new TextDecoder().decode(plain)) as {
      v?: number
      runId?: string
      savedAt?: number
      material?: unknown
    }
    if (value.v !== 1 || value.runId !== runId) return null
    const material = materialFromJson(value.material)
    if (material.header.runId && material.header.runId !== runId) return null
    return {
      seq: material.header.lastSeq,
      epoch: sealed.epoch,
      savedAt: value.savedAt ?? 0,
      material,
    }
  }

  async save(runId: string, material: RunMaterial): Promise<void> {
    if (this.locked) return
    const k = await this.keys.current()
    if (!k || this.locked) return
    const box = await sealContent(
      k.key,
      this.context(runId, k.epoch),
      utf8(
        JSON.stringify({ v: 1, runId, savedAt: Date.now(), material: materialToJson(material) }),
      ),
    )
    if (this.locked) return
    await idb.set(this.slot(runId), { epoch: k.epoch, box } satisfies Sealed)
  }
}

/** Quiet period after which a live stream with nothing more to replay counts as caught up. */
export const SYNC_GRACE_MS = 2000
/** Give up holding when the known head is never reached (stale head count, empty run). */
export const SYNC_STALL_MS = 10_000

/**
 * Pure sync decision. With a materialized state the verdict is exact at its seq, so it is shown
 * at once and only time-based verdicts (stalled) are held while the tail after it streams in.
 * Without one (nothing cached, no daemon snapshot) no verdict is shown until the replay is done.
 */
export function syncState(o: {
  view: Pick<TranscriptView, 'lastSeq' | 'connection' | 'material'>
  store: Pick<RunStore, 'caughtUp' | 'replaying'>
  headSeq?: number
  grace: boolean
}): TranscriptSync {
  if (o.view.connection === 'offline') return { syncing: false }
  const caughtUp =
    (o.store.caughtUp && o.view.lastSeq >= (o.headSeq ?? 0)) ||
    (o.grace && !o.store.replaying) ||
    (o.headSeq != null && o.headSeq > 0 && o.view.lastSeq >= o.headSeq && !o.store.replaying)
  if (caughtUp) return { syncing: false }
  const m = o.view.material
  return m && m.header.lastSeq > 0 ? { syncing: true, held: materialInput(m) } : { syncing: true }
}

/** The viewer's catch-up state for one run (the store seeds and caches its own material). */
export function useProgressSync(
  runId: string,
  view: TranscriptView,
  store: Pick<RunStore, 'caughtUp' | 'replaying'>,
  headSeq?: number,
): TranscriptSync {
  // Grace: the stream is live and nothing new arrived for a while. Short once the known head is
  // reached; long while it is not (the first page may still be decrypting).
  const [grace, setGrace] = useState(false)
  const reached = view.lastSeq > 0 && view.lastSeq >= (headSeq ?? 0)
  // biome-ignore lint/correctness/useExhaustiveDependencies: restart the quiet timer per seq
  useEffect(() => {
    setGrace(false)
    if (view.connection !== 'live') return
    const t = setTimeout(() => setGrace(true), reached ? SYNC_GRACE_MS : SYNC_STALL_MS)
    return () => clearTimeout(t)
  }, [view.connection, view.lastSeq, runId, reached])
  return syncState({ view, store, ...(headSeq != null ? { headSeq } : {}), grace })
}
